import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, Sparkles, Loader2, Receipt, Info, ArrowUpRight, ArrowRight,
  Zap, Camera, FileText, Code2, Wallet, History, CreditCard,
  AlertTriangle, TrendingUp, Clock, ShieldCheck, Gauge, Coins,
  XCircle, Calendar,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Stat, SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { Skeleton, EmptyState, Spinner, Dialog } from '../components/Dialog';
import { CheckoutDialog } from '../components/CheckoutDialog';
import { LoadMore } from '../components/LoadMore';
import { apiFetch } from '../api';
import useLoadMore from '../hooks/useLoadMore';
import {
  cn, formatCompact, formatNumber, formatRelative, formatUSD,
} from '../lib/utils';

/**
 * BillingPage — manage subscription, view plan cards, payment history.
 *
 * The companion topup flow lives at /topup (TopupPage).
 *
 * Layout:
 *   1. Hero — current plan + "manage subscription" overview
 *   2. Plan cards — every plan, with all the details (price / refill /
 *      included calls / rate limit / features)
 *   3. Per-call pricing matrix (5 cards with hover details)
 *   4. Payment history — every payment attempt (subscriptions + topups)
 */

// Local helper — wraps the imported formatNumber so the page can use a
// short alias everywhere. Imports `formatNumber` from utils for the
// full-format version (with thousands separators).
function fmt(n) {
  return formatNumber(n);
}

// Plan-tier comparison (used to detect upgrades between tiers).
// Higher number = higher tier.
const PLAN_TIER = { free: 0, starter: 1, standard: 2, pro: 3, enterprise: 4 };

/**
 * decidePlanCardCta — figure out what button to show on a plan card.
 *
 * Inputs:
 *   - plan:           the plan the user is looking at
 *   - billing:        the user's current billing summary from /v1/billing
 *   - userOnThisPlan: boolean — is `plan.code` the user's current plan?
 *
 * Returns one of:
 *   { type: 'current', label: 'Current plan' }
 *   null                              — Custom plans not offered
 *   { type: 'switch_quarterly',  ... } — same plan, currently monthly, switch to 3-month
 *   { type: 'switch_monthly', ... } — same plan, currently quarterly, switch to monthly
 *   { type: 'upgrade',        ... } — strictly higher tier
 *   null                              — strictly lower tier (downgrade not offered)
 *   { type: 'no_action',      ... } — fallback
 */
function decidePlanCardCta(plan, billing, userOnThisPlan) {
  const currentPlan = billing?.plan_code || 'free';
  const currentInterval = billing?.billing_interval || 'monthly';
  const currentTier = PLAN_TIER[currentPlan] ?? 0;
  const newTier = PLAN_TIER[plan.code] ?? 0;

  if (userOnThisPlan) {
    // Same plan as current. Options depend on interval.
    if (currentInterval === 'monthly' && plan.monthly_price_cents > 0 && plan.quarterly_price_cents > 0) {
      return {
        type: 'switch_quarterly',
        label: 'Switch to 3-month',
        plan,
        billingInterval: 'quarterly',
      };
    }
    if (currentInterval === 'quarterly') {
      return {
        type: 'switch_monthly',
        label: 'Switch to monthly',
        plan,
        billingInterval: 'monthly',
      };
    }
    return { type: 'current', label: 'Current plan' };
  }

  // Different plan. Downgrades aren't offered — only show the upgrade
  // CTA when the new plan is at the same tier or higher. For lower tiers
  // we hide the action entirely (return null so PlanCard renders nothing).
  if (newTier >= currentTier) {
    return { type: 'upgrade', label: `Upgrade to ${plan.display_name}`, plan, billingInterval: 'monthly' };
  }
  return null;
}

export default function BillingPage({ billing, onBillingChange }) {
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkout, setCheckout] = useState(null);
  // Discount % from the server (so we don't recompute the math on the client).
  const [quarterlyDiscountBps, setQuarterlyDiscountBps] = useState(2000);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        apiFetch('/payments/plans').catch(() => ({ plans: [] })),
        apiFetch('/payments/history').catch(() => ({ payments: [] })),
      ]);
      setPlans(p.plans || []);
      setPayments(h.payments || []);
      if (typeof p.quarterly_discount_bps === 'number') {
        setQuarterlyDiscountBps(p.quarterly_discount_bps);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase-16: pagination for the payment history table (10 per page).
  const paymentsPagination = useLoadMore(payments, 10);

  const currentPlan = plans.find((p) => p.code === billing?.plan_code);
    const currentPlanIndex = plans.findIndex((p) => p.code === billing?.plan_code);

    // Only render self-serve plans (free, starter, standard, pro, enterprise).
    // The dev-only 'test_dev' plan and the deprecated 'custom' plan are filtered out.
    const mainPlans = plans.filter((p) => p.code !== 'custom' && p.code !== 'test_dev');

  return (
    <div className="space-y-6 sm:space-y-8">
      <SectionHeader
        title="Subscription"
        description="Manage your plan, switch tiers, and review your billing history."
        actions={
          <Link to="/topup">
            <Button variant="secondary" size="sm">
              <Wallet className="h-3.5 w-3.5" />
              Top up credits
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        }
      />

      {/* Current plan overview */}
      <CurrentPlanOverview
        billing={billing}
        currentPlan={currentPlan}
        plans={plans}
        loading={loading && !billing}
        onUpgradeClick={(action) => {
          // The hero card's "Switch to 3-month/monthly" button.
          // Opens the CheckoutDialog with the right plan + interval.
          if (!action?.plan) return;
          const interval = action.billingInterval || 'quarterly';
          setCheckout({
            kind: 'subscription',
            planCode: action.plan.code,
            planName: action.plan.display_name,
            billingInterval: interval,
            usdCents:
              interval === 'quarterly'
                ? (action.plan.quarterly_price_cents || 0)
                : (action.plan.monthly_price_cents || 0),
          });
        }}
      />

      {/* Plan cards */}
      <div>
        <div className="flex items-baseline justify-between mb-4 px-1">
          <div>
            <h2 className="text-lg font-semibold text-fg">Choose a plan</h2>
            <p className="text-xs text-fg-muted mt-0.5">
              All plans include Cloudflare bypass, all output formats, and dashboard access.
            </p>
          </div>
          <p className="text-xs text-fg-muted">Prices in USD · Crypto via NOWPayments</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        ) : (
          <>
          {/* First 4 plans: 4-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {mainPlans.map((plan) => (
              <PlanCard
                key={plan.code}
                plan={plan}
                billing={billing}
                current={plan.code === billing?.plan_code}
                recommended={
                  plan.code === 'pro' && currentPlanIndex <= 1
                }
                quarterlyDiscountBps={quarterlyDiscountBps}
                onSelect={(action) => {
                  if (!action) return;
                  if (action.type === 'current') return;
                  // upgrade, switch_quarterly, switch_monthly — open checkout
                  setCheckout({
                    kind: 'subscription',
                    planCode: action.plan.code,
                    planName: action.plan.display_name,
                    billingInterval: action.billingInterval,
                    usdCents:
                      action.billingInterval === 'quarterly'
                        ? action.plan.quarterly_price_cents
                        : action.plan.monthly_price_cents,
                  });
                }}
              />
            ))}
          </div>
      </>
      )}
        </div>

      {/* Per-call pricing matrix */}
      <PerCallPricing perCallCosts={billing?.per_call_costs} />

      {/* Payment history */}
      <Card>
        <Card.Header>
          <div>
            <Card.Title>Payment history</Card.Title>
            <Card.Subtitle>Subscriptions, top-ups, and every crypto payment attempt.</Card.Subtitle>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center"><Spinner size={20} /></div>
          ) : payments.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={Receipt}
                title="No payments yet"
                description="When you upgrade or top up, payments will appear here."
              />
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-border bg-surface-2/30">
                <tr className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Type</th>
                  <th className="text-left px-5 py-3">Plan</th>
                  <th className="text-right px-5 py-3">Amount</th>
                  <th className="text-right px-5 py-3">Credits</th>
                  <th className="text-right px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentsPagination.visible.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-b-0 hover:bg-surface-2/40 transition-colors">
                    <td className="px-5 py-3.5 text-xs text-fg-muted">
                      <div>{p.created_at ? formatRelative(p.created_at) : '—'}</div>
                      {p.pay_currency && (
                        <div className="text-2xs text-fg-subtle mt-0.5">
                          via {p.pay_currency.toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-xs">
                      <Badge variant={p.kind === 'subscription' ? 'brand' : 'default'}>
                        {p.kind === 'subscription' ? 'Subscription' : 'Top-up'}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-fg capitalize">
                      {p.plan_code || '—'}
                    </td>
                    <td className="px-5 py-3.5 text-xs text-fg text-right tabular-nums">
                      {formatUSD(p.amount_cents)}
                    </td>
                    <td className="px-5 py-3.5 text-xs text-fg text-right tabular-nums">
                      {formatNumber(p.credits || 0)}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <Badge variant={
                        p.status === 'finished' ? 'success' :
                        p.status === 'failed' ? 'destructive' :
                        p.status === 'waiting' ? 'warning' : 'default'
                      }>
                        {p.status === 'finished' ? 'Paid' :
                         p.status === 'failed' ? 'Failed' :
                         p.status === 'waiting' ? 'Pending' :
                         p.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && payments.length > 0 && (
            <div className="border-t border-border p-3">
              <LoadMore
                hasMore={paymentsPagination.hasMore}
                onLoadMore={paymentsPagination.loadMore}
                visibleCount={paymentsPagination.visibleCount}
                total={paymentsPagination.total}
                pageSize={10}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Unified checkout dialog */}
      <CheckoutDialog
        open={!!checkout}
        kind={checkout?.kind}
        planCode={checkout?.planCode}
        planName={checkout?.planName}
        billingInterval={checkout?.billingInterval || 'monthly'}
        usdCents={checkout?.usdCents || 0}
        onClose={() => { setCheckout(null); onBillingChange?.(); }}
        onSuccess={() => { setCheckout(null); onBillingChange?.(); }}
      />

      {/* Downgrade confirmation modal */}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Current plan overview
// ---------------------------------------------------------------------------

function CurrentPlanOverview({ billing, currentPlan, loading, plans = [], onUpgradeClick }) {
  const credits = billing?.credit_balance ?? 0;
  const refill = currentPlan?.monthly_credit_refill ?? 0;
  const used = Math.max(0, refill - credits);
  const usedPct = refill > 0 ? Math.min(1, used / refill) : 0;
  // Phase-15: renamed from active_subscription_end to subscription_end
  const periodEnd = billing?.subscription_end;
  const periodStart = billing?.subscribed_date;
  const billingInterval = billing?.billing_interval || 'monthly';
  const renewalDay = billing?.renewal_day;
  const scheduledPlanCode = billing?.scheduled_plan_code;
  const scheduledInterval = billing?.scheduled_billing_interval;
  const intervalLabel = billingInterval === 'quarterly' ? '3-month' : 'Monthly';

  // Phase-18: prices — use the API-provided quarterly_price_cents when available
  // (don't recompute the discount client-side; the server is the source of truth).
  const currentPlanFromList = plans.find((p) => p.code === billing?.plan_code) || currentPlan;
  const monthlyPrice = currentPlanFromList?.monthly_price_cents || currentPlan?.monthly_price_cents || 0;
  const quarterlyPrice = currentPlanFromList?.quarterly_price_cents || 0;
  const intervalPrice = monthlyPrice > 0
    ? (billingInterval === 'quarterly'
        ? formatUSD(quarterlyPrice)
        : formatUSD(monthlyPrice))
    : 'Free';
  const intervalSuffix = billingInterval === 'quarterly' ? '/3mo' : '/mo';

  // Per-month equivalent + savings for the "Switch to 3-month" CTA.
  const quarterlyPerMonth = quarterlyPrice > 0 ? Math.round(quarterlyPrice / 3) : 0;
  const quarterlyTotalSavings = quarterlyPrice > 0 ? (monthlyPrice * 3 - quarterlyPrice) : 0;
  const showQuarterlyUpsell =
    monthlyPrice > 0 &&
    quarterlyPrice > 0 &&
    billingInterval === 'monthly' &&
    currentPlan?.code !== 'custom' &&
    currentPlan?.code !== 'free';
  const formatDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return null; }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Big current-plan card */}
      <Card variant="brand" className="lg:col-span-2 relative overflow-hidden">
        <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
        <div className="absolute -left-8 -bottom-12 h-40 w-40 rounded-full bg-amber-400/8 blur-3xl pointer-events-none" />
        <Card.Header className="relative">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand" />
            <span className="text-xs font-medium text-fg-muted uppercase tracking-wider">
              Current subscription
            </span>
          </div>
        </Card.Header>
        <Card.Body className="relative pt-0">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : (
            <>
              <div className="flex items-end gap-3 flex-wrap">
                <h2 className="text-4xl font-semibold text-fg tracking-tight">
                  {currentPlan?.display_name || billing?.plan_display_name || 'Free'}
                </h2>
                <Badge variant="brand">Active</Badge>
                <Badge variant="default">{intervalLabel}</Badge>
              </div>
              {currentPlan?.description && (
                <p className="text-sm text-fg-muted mt-2 max-w-lg">
                  {currentPlan.description}
                </p>
              )}
              {scheduledPlanCode && (
                <div className="mt-3 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs text-amber-700 dark:text-amber-300">
                  Scheduled change to <strong>{scheduledPlanCode}</strong>
                  {scheduledInterval && <> ({scheduledInterval})</>} on the next renewal date.
                </div>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-4 text-sm">
                <div>
                  <div className="text-xs text-fg-subtle">{intervalLabel}</div>
                  <div className="text-fg font-semibold tabular-nums">
                    {intervalPrice}{intervalPrice !== 'Free' && <span className="text-xs text-fg-muted font-normal">{intervalSuffix}</span>}
                  </div>
                </div>
                <div className="h-8 w-px bg-border" />
                <div>
                  <div className="text-xs text-fg-subtle">Refill</div>
                  <div className="text-fg font-semibold tabular-nums">
                    {formatNumber(refill || 0)} credits
                  </div>
                </div>
                {currentPlan?.rate_limit_per_minute ? (
                  <>
                    <div className="h-8 w-px bg-border" />
                    <div>
                      <div className="text-xs text-fg-subtle">Concurrent requests</div>
                      <div className="text-fg font-semibold tabular-nums">
                        {currentPlan.rate_limit_per_minute}
                      </div>
                    </div>
                  </>
                ) : null}
                {periodEnd && (
                  <>
                    <div className="h-8 w-px bg-border" />
                    <div>
                      <div className="text-xs text-fg-subtle">Renews</div>
                      <div className="text-fg font-semibold">
                        {formatRelative(periodEnd)}
                        {renewalDay && <span className="text-2xs text-fg-subtle ml-1">(day {renewalDay})</span>}
                      </div>
                      <div className="text-2xs text-fg-subtle mt-0.5">
                        {formatDate(periodEnd)}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {periodStart && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-fg-muted">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Subscribed on <strong className="text-fg font-medium">{formatDate(periodStart)}</strong></span>
                </div>
              )}

              {/* Phase-18: prominent "Switch to 3-month" CTA inside the
                  current subscription card. Shows the per-month savings
                  so the user can see the discount without leaving the page. */}
              {showQuarterlyUpsell && (
                <div className="mt-5 px-4 py-3 rounded-lg bg-gradient-to-br from-emerald-500/10 to-amber-500/5 border border-emerald-500/30">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                        <Sparkles className="h-3.5 w-3.5" />
                        <span>Save {formatUSD(quarterlyTotalSavings)} every 3 months by switching to quarterly</span>
                      </div>
                      <div className="text-xs text-fg-muted mt-0.5">
                        Same plan — just billed quarterly at <strong className="text-fg">{formatUSD(quarterlyPerMonth)}/mo</strong> instead of {formatUSD(monthlyPrice)}/mo.
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onUpgradeClick?.({
                        plan: currentPlanFromList || currentPlan,
                        billingInterval: 'quarterly',
                      })}
                    >
                      Switch to 3-month
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
              {billingInterval === 'quarterly' && monthlyPrice > 0 && (
                <div className="mt-5 px-4 py-3 rounded-lg bg-surface-2/60 border border-border">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-fg">
                        You're on the quarterly plan
                      </div>
                      <div className="text-xs text-fg-muted mt-0.5">
                        You pay <strong className="text-fg">{formatUSD(quarterlyPrice)}/3mo</strong>
                        {' '}(= {formatUSD(quarterlyPerMonth)}/mo).
                        {' '}Switch to monthly if you'd rather pay month-to-month.
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onUpgradeClick?.({
                        plan: currentPlanFromList || currentPlan,
                        billingInterval: 'monthly',
                      })}
                    >
                      Switch to monthly
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Credit usage bar */}
      <Card>
        <Card.Header>
          <div>
            <Card.Title>This cycle</Card.Title>
            <Card.Subtitle>Credits used vs monthly refill.</Card.Subtitle>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            <div>
              <div className="flex items-baseline justify-between">
                <div className="text-2xs text-fg-muted uppercase tracking-wider">Used</div>
                <div className="text-2xl font-semibold text-fg tabular-nums">
                  {formatNumber(used)}
                </div>
              </div>
              <div className="mt-2 h-2 bg-surface-2 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full transition-all duration-700 rounded-full',
                    usedPct >= 0.9 ? 'bg-red-500' : usedPct >= 0.7 ? 'bg-amber-500' : 'bg-brand'
                  )}
                  style={{ width: `${usedPct * 100}%` }}
                />
              </div>
              <div className="mt-2 text-2xs text-fg-subtle flex justify-between">
                <span>{formatNumber(credits)} of {formatNumber(refill || 0)} left</span>
                <span>{Math.round(usedPct * 100)}%</span>
              </div>
            </div>
            <div className="pt-3 border-t border-border space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-fg-muted">Total purchased</span>
                <span className="text-fg tabular-nums">{formatNumber(billing?.total_purchased || 0)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-fg-muted">Total consumed</span>
                <span className="text-fg tabular-nums">{formatNumber(billing?.total_consumed || 0)}</span>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan card
// ---------------------------------------------------------------------------

function PlanCard({ plan, billing, current, recommended, quarterlyDiscountBps = 2000, onSelect }) {
  const isFree = plan.code === 'free';

  const cta = decidePlanCardCta(plan, billing, current);
  const discountPct = quarterlyDiscountBps / 100;  // for display ("Save 20%")

  // Price display: show whichever interval the picker is set to.
  // The user picks monthly or quarterly BEFORE clicking the CTA, so the
  // displayed price matches what they'll pay in checkout.
  // Default is quarterly (the discounted price); clicking Monthly swaps
  // it to the original per-month price with the quarterly crossed out.
  const showBothPrices = !isFree && plan.monthly_price_cents > 0 && plan.quarterly_price_cents > 0;
  const quarterlyPerMonthCents = showBothPrices ? Math.round(plan.quarterly_price_cents / 3) : 0;
  const quarterlyTotalCents = showBothPrices ? plan.quarterly_price_cents : 0;
  const quarterlySavingsCents = showBothPrices
    ? (plan.monthly_price_cents * 3) - plan.quarterly_price_cents
    : 0;

  // Phase-18: local interval picker for the card. Defaults to 3-month
  // (so the user sees the discounted price first — designed to nudge
  // toward quarterly subscriptions per the spec). Clicking "Monthly"
  // switches the price back to the non-discounted monthly amount.
  const [pickedInterval, setPickedInterval] = useState('monthly');
  // Default the picker to whichever interval is currently selected for
  // the user's plan (when on this plan). Helps avoid accidental switch.
  useEffect(() => {
    if (current && billing?.billing_interval) {
      setPickedInterval(billing.billing_interval);
    }
  }, [current, billing?.billing_interval]);
  // Compute the CTA we should show given the user's *picked* interval.
  // If the user is on this plan monthly and picks quarterly, the CTA becomes
  // "Switch to 3-month" (with the quarterly price).
  const effectiveCta = useMemo(() => {
    if (isFree) return cta;
    if (current) {
      // The user is already on this plan; the CTA flips between
      // "switch_quarterly" / "switch_monthly" depending on what they picked.
      if (pickedInterval === 'quarterly' && billing?.billing_interval !== 'quarterly') {
        return {
          type: 'switch_quarterly',
          label: 'Switch to 3-month',
          plan,
          billingInterval: 'quarterly',
        };
      }
      if (pickedInterval === 'monthly' && billing?.billing_interval === 'quarterly') {
        return {
          type: 'switch_monthly',
          label: 'Switch to monthly',
          plan,
          billingInterval: 'monthly',
        };
      }
    } else {
      // User is upgrading from another plan. They picked an interval.
      // Downgrades aren't offered, so the only branch here is upgrade.
      if (pickedInterval === 'quarterly') {
        return {
          type: 'upgrade',
          label: `Upgrade to ${plan.display_name} (3-month)`,
          plan,
          billingInterval: 'quarterly',
        };
      }
    }
    // No branch matched (e.g. user is on this plan with the same interval
    // they picked, and the plan isn't free). ``cta`` from
    // ``decidePlanCardCta`` is correct in that case — pass it through.
    return cta;
  }, [cta, current, pickedInterval, billing, plan, isFree]);
  const showIntervalPicker =
    showBothPrices &&
    (current || (PLAN_TIER[plan.code] ?? 0) >= (PLAN_TIER[billing?.plan_code || 'free'] ?? 0));

  return (
      <Card
        className={cn(
          'relative flex flex-col transition-all',
          current && 'ring-2 ring-brand/40 border-brand/50',
          recommended && !current && 'border-amber-500/40',
        )}
      >
        {/* Top badges */}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5 z-10">
          {current && <Badge variant="brand">Current</Badge>}
          {recommended && !current && (
            <Badge variant="warning">Recommended</Badge>
          )}
          {plan.code === 'enterprise' && !current && (
            <Badge variant="default">Top tier</Badge>
          )}
        </div>

        <Card.Header>
          <div>
            <Card.Title className="capitalize">{plan.display_name}</Card.Title>
            {plan.description && (
              <Card.Subtitle className="mt-1 line-clamp-2">
                {plan.description}
              </Card.Subtitle>
            )}
        </div>
      </Card.Header>

      <Card.Body className="pt-0 flex-1 flex flex-col gap-4">
        {/* Left column: price + metrics */}
        <div>
          {/* Price */}
          <div>
            <div className="flex items-baseline gap-1">
              {isFree ? (
                <span className="text-3xl font-semibold text-fg">Free</span>
              ) : showBothPrices ? (
              // Phase-18 redesign: price reflects the picked interval.
              //   - Default / picked monthly: big price = $X/mo, with the
              //     full 3-month price ($11.99 every 3 months) shown as a
              //     strong callout so the user always sees the actual charge
              //     for the 3-month option.
              //   - Picked quarterly: big price = the per-month equivalent
              //     of the quarterly price, with the monthly strikethrough.
              <div>
                {pickedInterval === 'quarterly' ? (
                  <>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-semibold text-fg tabular-nums">
                        {formatUSD(quarterlyPerMonthCents)}
                      </span>
                      <span className="text-sm text-fg-muted">/mo</span>
                      <span className="text-2xs font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                                                quarterly
                                              </span>
                    </div>
                    <div className="mt-1 text-xs text-fg-muted">
                      <span className="line-through opacity-70">
                        {formatUSD(plan.monthly_price_cents)}/mo
                      </span>
                      {' '}billed monthly · save{' '}
                      <strong className="text-fg">{formatUSD(quarterlySavingsCents)}</strong>
                      {' '}every 3 months
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-semibold text-fg tabular-nums">
                        {formatUSD(plan.monthly_price_cents)}
                      </span>
                      <span className="text-sm text-fg-muted">/mo</span>
                      <span className="text-2xs font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-3 text-fg-muted">
                        monthly
                      </span>
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="text-sm text-fg">
                        or{' '}
                        <strong className="tabular-nums text-fg">{formatUSD(plan.quarterly_price_cents)}</strong>
                        {' '}every 3 months
                        {' '}
                        <span className="text-2xs font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ml-1">
                          save {formatUSD(quarterlySavingsCents)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
                <>
                  <span className="text-3xl font-semibold text-fg tabular-nums">
                    {formatUSD(plan.monthly_price_cents)}
                  </span>
                  <span className="text-sm text-fg-muted">/mo</span>
                </>
              )}
            </div>
            {!isFree && plan.per_credit_cents > 0 && (
              <div className="text-2xs text-fg-subtle mt-1">
                or buy credits at {formatUSD(plan.per_credit_cents)}/credit
              </div>
            )}
            {false && (
              <div className="text-2xs text-fg-subtle mt-1">
                Volume pricing · tailored SLAs · custom contract
              </div>
            )}
          </div>

          <div className="rounded-lg bg-surface-2/50 border border-border divide-y divide-border mt-2">
            <Metric
              icon={Coins}
              label="Refill"
              value={
                pickedInterval === 'quarterly'
                  ? formatNumber(plan.quarterly_initial_credits || 0)
                  : formatNumber(plan.initial_credits || 0)
              }
            />
            <Metric
              icon={Gauge}
              label="Concurrent rate"
              value={`${plan.rate_limit_per_minute || 60}`}
            />
            {plan.hard_block ? (
              <Metric
                icon={AlertTriangle}
                label="Overage behavior"
                value="Hard block on 0 credits"
                tone="warning"
              />
            ) : (
              <Metric
                icon={Coins}
                label="Overage behavior"
                value={`$${(plan.over_usage_usd_per_1k || (plan.over_usage_cents_per_1k_credits / 100) || 0).toFixed(2)}/1K credits`}
              />
            )}
          </div>
        </div>

        {/* Right column: features */}
        {plan.features && plan.features.length > 0 && (
          <div className="flex-1">
            <div className="text-xs font-medium text-fg-muted uppercase tracking-wider mb-2">
              What you get
            </div>
            <ul className="space-y-1.5">
              {plan.features.map((feat) => (
                <li key={feat} className="flex items-start gap-2 text-xs text-fg">
                  <Check className="h-3.5 w-3.5 text-brand shrink-0 mt-0.5" />
                  <span>{feat}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card.Body>

      {/* CTA */}
      <Card.Footer>
        {/* Phase-18: interval picker. Lets the user choose monthly vs
            quarterly BEFORE clicking the CTA, so checkout opens with the
            right interval + price. Only shown for self-serve plans
            where quarterly is available and the user can actually switch
            (current plan OR upgrading to a same/higher tier). */}
        {showIntervalPicker && (
          <div className="mb-3">
            <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider mb-1.5 text-center">
              Bill me
            </div>
            <div className="inline-flex rounded-md border border-border bg-surface-2 p-0.5 w-full">
              <button
                type="button"
                onClick={() => setPickedInterval('monthly')}
                className={cn(
                  'flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors',
                  pickedInterval === 'monthly'
                    ? 'bg-surface text-fg shadow-sm'
                    : 'text-fg-muted hover:text-fg'
                )}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setPickedInterval('quarterly')}
                className={cn(
                  'flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors inline-flex items-center justify-center gap-1',
                  pickedInterval === 'quarterly'
                    ? 'bg-surface text-fg shadow-sm'
                    : 'text-fg-muted hover:text-fg'
                )}
              >
                3-month
                {showBothPrices && (
                  <span className={cn(
                    'text-2xs px-1 rounded',
                    pickedInterval === 'quarterly' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'text-emerald-600 dark:text-emerald-500'
                  )}>
                    −{discountPct}%
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
        {effectiveCta == null ? (
          // 2026-07-12: plan cards can have no CTA at all (e.g. the free
          // plan when the user is on a paid plan — downgrades aren't
          // offered so ``decidePlanCardCta`` returns ``null``).
          // Previously the renderer blindly did ``effectiveCta.type``
          // and crashed with "Cannot read properties of null (reading
          // 'type')" on the entire billing page. Render nothing.
          null
        ) : effectiveCta.type === 'current' ? (
          <Button variant="secondary" className="w-full" disabled>
            Current plan
          </Button>
          ) : effectiveCta.type === 'switch_quarterly' || effectiveCta.type === 'switch_monthly' ? (
            // Same plan, different interval — charges for the new interval.
            // The button shows the full quarterly price when switching to
            // 3-month billing, since that's what the customer will be charged.
            <Button
              variant={recommended ? 'primary' : 'default'}
              className="w-full"
              onClick={() => onSelect?.(effectiveCta)}
            >
              {effectiveCta.type === 'switch_quarterly'
                ? `Switch to 3-month — ${formatUSD(quarterlyTotalCents)} now`
                : `Switch to monthly — ${formatUSD(plan.monthly_price_cents)}/mo`}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          ) : effectiveCta.type === 'upgrade' ? (
            <Button
              variant={recommended ? 'primary' : 'default'}
              className="w-full"
              onClick={() => onSelect?.(effectiveCta)}
            >
              {/* For upgrades, use the price that matches the picked interval.
                When on the 3-month picker we show the full quarterly price
                (not the per-month equivalent) so the user sees exactly
                what they'll be charged. */}
              {pickedInterval === 'quarterly'
                ? `Upgrade to ${plan.display_name} — ${formatUSD(quarterlyTotalCents)} now`
                : `Upgrade to ${plan.display_name} — ${formatUSD(plan.monthly_price_cents)}/mo`}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          ) : (
            // Fallback: effectiveCta is null (lower-tier plans are hidden
            // because downgrades aren't offered). Render nothing.
            null
          )}
      </Card.Footer>
    </Card>
  );
}

function Metric({ icon: Icon, label, value, tone }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <Icon className={cn(
        'h-3.5 w-3.5 shrink-0',
        tone === 'warning' ? 'text-amber-500' : 'text-fg-muted'
      )} />
      <div className="flex-1 min-w-0">
        <div className="text-2xs text-fg-muted">{label}</div>
      </div>
      <div className={cn(
        'text-xs font-medium tabular-nums shrink-0',
        tone === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-fg'
      )}>
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-call pricing matrix
// ---------------------------------------------------------------------------

function PerCallPricing({ perCallCosts }) {
  const c = {
    classic_base: 1,
    stealth_base: 4,
    js_render_extra: 1,
    screenshot_extra: 2,
    pdf_extra: 1,
    ...(perCallCosts || {}),
  };
  const items = [
    {
      icon: Zap,
      label: 'Classic request',
      cost: c.classic_base,
      tone: 'text-fg-muted',
      detail: 'Standard HTTP via TLS client or browser. No challenge solving needed.',
    },
    {
      icon: Zap,
      label: 'Stealth request',
      cost: c.stealth_base,
      tone: 'text-amber-600 dark:text-amber-400',
      detail: 'Forces Datames to solve Cloudflare / Turnstile. Also the cost for dynamic mode when a challenge is detected.',
    },
    {
      icon: Code2,
      label: 'JS render',
      cost: `+${c.js_render_extra}`,
      tone: 'text-fg-muted',
      detail: 'Add js_render=true to render the page in a real browser. Stacks on top of base.',
    },
    {
      icon: Camera,
      label: 'Screenshot',
      cost: `+${c.screenshot_extra}`,
      tone: 'text-fg-muted',
      detail: 'screenshot=true. Fullpage OR CSS-selector screenshot. Stacks on top of base.',
    },
    {
      icon: FileText,
      label: 'PDF output',
      cost: `+${c.pdf_extra}`,
      tone: 'text-fg-muted',
      detail: 'response_type=pdf. Classic only — stealth rejects PDF.',
    },
  ];

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4 px-1">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-fg">Per-call pricing</h2>
          <Link
            to="/docs"
            className="inline-flex items-center justify-center h-5 w-5 rounded-full text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
            title="Credit cost depends on the engine that ran + extras. See more in the docs."
            aria-label="See more in docs"
          >
            <Info className="h-3.5 w-3.5" />
          </Link>
        </div>
        <p className="text-xs text-fg-muted">Failed requests still cost — no refunds.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className="rounded-lg border border-border bg-surface p-4 hover:border-border-strong transition-colors"
              title={item.detail}
            >
              <div className="flex items-center gap-2">
                <Icon className={cn('h-4 w-4', item.tone)} />
                <span className="text-xs font-medium text-fg-muted uppercase tracking-wider">
                  {item.label}
                </span>
              </div>
              <div className="mt-2 text-2xl font-semibold tabular-nums text-fg">
                {item.cost}
                <span className="ml-1 text-xs font-normal text-fg-muted">credits</span>
              </div>
              <p className="mt-1.5 text-2xs text-fg-muted leading-snug line-clamp-3">
                {item.detail}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
